import React, { useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { flattenToAppURL } from '@plone/volto/helpers';

const getHref = (link) => {
  const item = Array.isArray(link) ? link[0] : link;
  if (!item) return '#';
  const url = item['@id'] || '#';
  const isFile = item['@type'] === 'File';
  return isFile ? `${url}/@@download/file` : url;
};

const extractTOCEntries = (blocks, layout, useH2 = true, useH3 = true, useBlockTitles = false, selfBlockId = null) => {
  const entries = [];
  const allowedTypes = [
    ...(useH2 ? ['heading-two', 'h2'] : []),
    ...(useH3 ? ['heading-three', 'h3'] : []),
  ];

  layout.forEach((blockId) => {
    if (blockId === selfBlockId) return;
    const block = blocks[blockId];
    
    // Extract from slate/text blocks (headings)
    if ((block?.['@type'] === 'text' || block?.['@type'] === 'slate') && Array.isArray(block.value)) {
      block.value.forEach((node) => {
        if (allowedTypes.includes(node.type)) {
          const text = node.children?.map((child) => child.text).join('').trim();
          const anchor = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
          if (text) {
            entries.push({ id: anchor, title: text });
          }
        }
      });
    }
    
    // Extract from block titles (if enabled)
    if (useBlockTitles && block) {
      const titleField = block.headline || block.title || block.heading || block.headerText;
      if (titleField && typeof titleField === 'string' && titleField.trim()) {
        const text = titleField.trim();
        const anchor = text
          .toLowerCase()
          .replace(/<[^>]*>/g, '')
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .trim();
        entries.push({ id: anchor, title: text, isBlockTitle: true, blockId });
      }
    }
  });

  return entries;
};

// Get a class name from the background color value
const getBackgroundColorClass = (backgroundColor) => {
  if (!backgroundColor || backgroundColor === '') return 'bg-gradient';
  if (backgroundColor === 'none') return 'bg-none';
  
  // Extract the rgba values to determine the color
  if (backgroundColor.includes('0, 0, 0')) return 'bg-black-overlay';
  if (backgroundColor.includes('255, 255, 255')) return 'bg-white-overlay';
  if (backgroundColor.includes('var(--accent-color-rgb)')) return 'bg-primary-overlay';
  
  return 'bg-custom-overlay';
};

// Resolve the best available preview image URL from page properties.
// Priority:
//   1. preview_image_link — linked preview image (separate Image object)
//   2. properties.preview_image — embedded preview image
//   3. properties.image — embedded lead image
const getPreviewImageUrl = (properties) => {
  const baseUrl = flattenToAppURL(properties?.['@id'] || '');

  // 1. Linked preview image — relation object with nested image_scales
  const linkedImage = properties?.preview_image_link;
  if (linkedImage?.['@id']) {
    const linkedBase = flattenToAppURL(linkedImage['@id']);
    const scales = linkedImage?.image_scales?.image?.[0]?.scales;
    if (scales?.large?.download) {
      return `${linkedBase}/${scales.large.download}`;
    }
    // Fallback: construct URL directly
    return `${linkedBase}/@@images/image/large`;
  }

  // 2. Embedded preview_image field
  if (properties?.preview_image) {
    return `${baseUrl}/@@images/preview_image/large`;
  }

  // 3. Embedded image field (lead image)
  if (properties?.image) {
    return `${baseUrl}/@@images/image/large`;
  }

  return '';
};

const HeroItem = ({ hero, properties, isFirst, usePageOptions, blocks, layout, displayMode, sharedBackgroundUrl, sharedVideoUrl, heroIndex, carouselMode, breadcrumbItems, selfBlockId }) => {
  const pageTitle = properties?.title || '';
  const pageDescription = properties?.description || '';
  const baseUrl = properties?.['@id'] || '';

  // Determine title and subtitle
  let displayTitle = hero.title;
  let displaySubtitle = hero.subtitle;
  let displayPreheader = hero.preheader;
  let publicationDate = null;
  let formattedDate = null;

  if (isFirst && usePageOptions) {
    if (usePageOptions.usePageTitle) {
      displayTitle = pageTitle;
    }
    if (usePageOptions.usePageDescription) {
      displaySubtitle = pageDescription;
    }
    if (usePageOptions.showPublicationDate) {
      publicationDate = properties?.effective;
      formattedDate = publicationDate ? new Date(publicationDate).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      }) : null;
      displayPreheader = formattedDate;
    }
  }

  // Determine background
  let imageUrl = '';
  let videoUrl = null;

  if (displayMode === 'shared' && sharedBackgroundUrl) {
    imageUrl = sharedBackgroundUrl;
    videoUrl = sharedVideoUrl;
  } else {
    // Individual background
    if (isFirst && usePageOptions?.usePreviewImage) {
      imageUrl = getPreviewImageUrl(properties);
    } else {
      const image = hero.backgroundImage?.[0];
      imageUrl = image ? `${image['@id']}/@@images/image` : '';
    }

    // Video
    const rawId = hero.backgroundVideo?.[0]?.['@id'] || '';
    const filename = rawId.split('/').filter(Boolean).pop();
    const isAlreadyDownloadUrl = rawId.includes('/@@download/');
    videoUrl = rawId && filename
      ? (isAlreadyDownloadUrl ? rawId : `${rawId}/@@download/file/${filename}`)
      : null;
  }

  // Logo
  const logoImage = hero.heroLogo?.[0];
  const logoUrl = logoImage ? `${logoImage['@id']}/@@images/image` : null;
  const logoPosition = hero.logoPosition || 'above-title';
  const logoSize = hero.logoSize || 'medium';

  // TOC - only for first hero
  let tocEntries = [];
  if (isFirst && hero.useTOC && blocks && layout) {
    tocEntries = extractTOCEntries(blocks, layout, hero.useH2 ?? true, hero.useH3 ?? true, hero.useBlockTitles ?? false, selfBlockId);
  }

  const alignment = hero.alignment || 'left';
  const backgroundColor = hero.backgroundColor || '';
  const foregroundColor = hero.foregroundColor || 'light';
  const topPaddingDesktop = hero.topPaddingDesktop || 'default';
  const topPaddingMobile = hero.topPaddingMobile || 'default';
  const backgroundPosition = hero.backgroundPosition || 'center';
  
  // Determine if we should show gradient overlay
  const showGradient = !backgroundColor || backgroundColor === '';
  const showColorOverlay = backgroundColor && backgroundColor !== '' && backgroundColor !== 'none';
  
  // Get background color class for styling hooks
  const bgColorClass = getBackgroundColorClass(backgroundColor);

  const TitleTag = isFirst ? 'h1' : 'h2';
  
  // Breadcrumbs — only on first hero when enabled
  const showBreadcrumbs = isFirst && usePageOptions?.showBreadcrumbs && breadcrumbItems?.length > 0;
  const allCrumbs = showBreadcrumbs
    ? [{ url: '/', title: 'Home' }, ...breadcrumbItems]
    : [];
  const heroBreadcrumbs = showBreadcrumbs ? (
    <div className="hero-preheader hero-breadcrumbs">
      {allCrumbs.slice(0, -1).map((crumb, i, arr) => {
        const href = crumb.url === '/' ? '/' : flattenToAppURL(crumb.url);
        return (
          <span key={i}>
            <a href={href}>{crumb.title}</a>
            <span className="hero-breadcrumb-separator" aria-hidden="true"> / </span>
          </span>
        );
      })}
    </div>
  ) : null;
  
  // Wrapper class for carousel slide
  const wrapperClass = carouselMode ? 'embla__slide' : `hero-item hero-item-${heroIndex + 1}`;

  return (
    <div className={`${wrapperClass} ${bgColorClass} foreground-${foregroundColor} padding-desktop-${topPaddingDesktop} padding-mobile-${topPaddingMobile}`}>
      {displayMode !== 'shared' && (
        <>
          {videoUrl ? (
            <video
              className="hero-video-bg"
              autoPlay
              muted
              loop
              playsInline
              poster={imageUrl}
            >
              <source src={videoUrl} type="video/mp4" />
            </video>
          ) : (
            <div
              className="hero-image-bg"
              style={{ backgroundImage: `url(${imageUrl})`, backgroundPosition }}
            />
          )}
        </>
      )}
      {showColorOverlay && (
        <div className="hero-color-overlay" style={{ backgroundColor }} />
      )}
      {showGradient && <div className="overlay" />}
      <div className={`custom-hero-inner align-${alignment} ${hero.horizontalLayout ? 'horizontal-layout' : ''} ${logoUrl && logoPosition === 'beside-content' ? 'logo-beside-layout' : ''}`}>
        {/* Logo: above-title position */}
        {logoUrl && logoPosition === 'above-title' && (
          <div className={`hero-logo hero-logo-above hero-logo-${logoSize}`}>
            <img src={logoUrl} alt="" />
          </div>
        )}

        {/* Beside-content layout: content left, logo right */}
        {logoUrl && logoPosition === 'beside-content' ? (
          <div className="hero-logo-beside-wrapper">
            <div className="hero-logo-beside-content">
              {hero.horizontalLayout ? (
                <>
                  <div className="custom-hero-content">
                    {heroBreadcrumbs}
                    {displayPreheader && <div className="hero-preheader">{displayPreheader}</div>}
                    {displayTitle && <TitleTag>{displayTitle}</TitleTag>}
                  </div>
                  {(displaySubtitle || (isFirst && hero.useTOC ? tocEntries.length > 0 : hero.buttons?.length > 0)) && (
                    <div className="subtitle-buttons-wrapper">
                      {displaySubtitle && <div className="subtitle">{displaySubtitle}</div>}
                      {(isFirst && hero.useTOC ? tocEntries.length > 0 : hero.buttons?.length > 0) && (
                        <div className={`custom-hero-buttons${hero.smallButtons ? ' small-buttons' : ''}`}>
                          {isFirst && hero.useTOC
                            ? tocEntries.map((entry, i) => (
                                <a key={i} href={`#${entry.id}`} className="hero-button white-outline">
                                  {entry.title}
                                </a>
                              ))
                            : hero.buttons.map((btn, i) => {
                                const href = getHref(btn.link);
                                const buttonStyle = btn.buttonStyle || 'white-outline';
                                const isExternal = (() => {
                                  try {
                                    const url = new URL(href, window.location.origin);
                                    return url.hostname !== window.location.hostname;
                                  } catch { return false; }
                                })();
                                return (
                                  <a key={i} href={href} className={`hero-button ${buttonStyle}`}
                                    {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                                    {btn.label || 'Click here'}
                                  </a>
                                );
                              })}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="custom-hero-content">
                    {heroBreadcrumbs}
                    {displayPreheader && <div className="hero-preheader">{displayPreheader}</div>}
                    {displayTitle && <TitleTag>{displayTitle}</TitleTag>}
                    {displaySubtitle && <div className="subtitle">{displaySubtitle}</div>}
                  </div>
                  {(isFirst && hero.useTOC ? tocEntries.length > 0 : hero.buttons?.length > 0) && (
                    <div className={`custom-hero-buttons${hero.smallButtons ? ' small-buttons' : ''}`}>
                      {isFirst && hero.useTOC
                        ? tocEntries.map((entry, i) => (
                            <a key={i} href={`#${entry.id}`} className="hero-button white-outline">
                              {entry.title}
                            </a>
                          ))
                        : hero.buttons.map((btn, i) => {
                            const href = getHref(btn.link);
                            const buttonStyle = btn.buttonStyle || 'white-outline';
                            const isExternal = (() => {
                              try {
                                const url = new URL(href, window.location.origin);
                                return url.hostname !== window.location.hostname;
                              } catch { return false; }
                            })();
                            return (
                              <a key={i} href={href} className={`hero-button ${buttonStyle}`}
                                {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                                {btn.label || 'Click here'}
                              </a>
                            );
                          })}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className={`hero-logo hero-logo-beside hero-logo-${logoSize}`}>
              <img src={logoUrl} alt="" />
            </div>
          </div>
        ) : hero.horizontalLayout ? (
          <>
            <div className="custom-hero-content">
              {heroBreadcrumbs}
                    {displayPreheader && <div className="hero-preheader">{displayPreheader}</div>}
              {displayTitle && <TitleTag>{displayTitle}</TitleTag>}
            </div>
            {(displaySubtitle || (isFirst && hero.useTOC ? tocEntries.length > 0 : hero.buttons?.length > 0)) && (
              <div className="subtitle-buttons-wrapper">
                {displaySubtitle && <div className="subtitle">{displaySubtitle}</div>}
                {(isFirst && hero.useTOC ? tocEntries.length > 0 : hero.buttons?.length > 0) && (
                  <div className={`custom-hero-buttons${hero.smallButtons ? ' small-buttons' : ''}`}>
                    {isFirst && hero.useTOC
                      ? tocEntries.map((entry, i) => (
                          <a key={i} href={`#${entry.id}`} className="hero-button white-outline">
                            {entry.title}
                          </a>
                        ))
                      : hero.buttons.map((btn, i) => {
                          const href = getHref(btn.link);
                          const buttonStyle = btn.buttonStyle || 'white-outline';
                          const isExternal = (() => {
                            try {
                              const url = new URL(href, window.location.origin);
                              return url.hostname !== window.location.hostname;
                            } catch {
                              return false;
                            }
                          })();
                          return (
                            <a
                              key={i}
                              href={href}
                              className={`hero-button ${buttonStyle}`}
                              {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                            >
                              {btn.label || 'Click here'}
                            </a>
                          );
                        })}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="custom-hero-content">
              {heroBreadcrumbs}
                    {displayPreheader && <div className="hero-preheader">{displayPreheader}</div>}
              {displayTitle && <TitleTag>{displayTitle}</TitleTag>}
              {displaySubtitle && <div className="subtitle">{displaySubtitle}</div>}
            </div>
            {(isFirst && hero.useTOC ? tocEntries.length > 0 : hero.buttons?.length > 0) && (
              <div className={`custom-hero-buttons${hero.smallButtons ? ' small-buttons' : ''}`}>
                {isFirst && hero.useTOC
                  ? tocEntries.map((entry, i) => (
                      <a key={i} href={`#${entry.id}`} className="hero-button white-outline">
                        {entry.title}
                      </a>
                    ))
                  : hero.buttons.map((btn, i) => {
                      const href = getHref(btn.link);
                      const buttonStyle = btn.buttonStyle || 'white-outline';
                      const isExternal = (() => {
                        try {
                          const url = new URL(href, window.location.origin);
                          return url.hostname !== window.location.hostname;
                        } catch {
                          return false;
                        }
                      })();
                      return (
                        <a
                          key={i}
                          href={href}
                          className={`hero-button ${buttonStyle}`}
                          {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        >
                          {btn.label || 'Click here'}
                        </a>
                      );
                    })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const CarouselControls = ({ emblaApi, hideNav, hideButtons, controlsColor }) => {
  const [prevBtnDisabled, setPrevBtnDisabled] = React.useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = React.useState(true);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [scrollSnaps, setScrollSnaps] = React.useState([]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index) => emblaApi && emblaApi.scrollTo(index), [emblaApi]);

  const onInit = useCallback((emblaApi) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);

  return (
    <div className={`embla__controls controls-${controlsColor}`}>
      {!hideButtons && (
        <div className="embla__buttons">
          <button
            className="embla__button embla__button--prev"
            onClick={scrollPrev}
            disabled={prevBtnDisabled}
          >
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button
            className="embla__button embla__button--next"
            onClick={scrollNext}
            disabled={nextBtnDisabled}
          >
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}
      {!hideNav && (
        <div className="embla__dots">
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              className={`embla__dot ${index === selectedIndex ? 'embla__dot--selected' : ''}`}
              type="button"
              onClick={() => scrollTo(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const View = (props) => {
  const { data, properties = {}, block: selfBlockId } = props;
  const breadcrumbItems = useSelector((state) => state.breadcrumbs?.items || []);

  useEffect(() => {
    if (
      data?.showPublicationDate &&
      document?.body?.classList?.contains('contenttype-news-item')
    ) {
      document.body.classList.add('publication-date-visible');
      return () => {
        document.body.classList.remove('publication-date-visible');
      };
    }
  }, [data?.showPublicationDate]);

  // Add anchor IDs to blocks with titles for TOC linking
  useEffect(() => {
    if (data?.useTOC && data?.useBlockTitles) {
      const timeoutId = setTimeout(() => {
        const blocks = properties?.blocks || {};
        const layout = properties?.blocks_layout?.items || [];
        
        layout.forEach((blockId) => {
          const block = blocks[blockId];
          const titleField = block?.headline || block?.title || block?.heading || block?.headerText;
          
          if (titleField && typeof titleField === 'string' && titleField.trim()) {
            const slug = titleField
              .trim()
              .toLowerCase()
              .replace(/<[^>]*>/g, '')
              .replace(/[^\w\s-]/g, '')
              .replace(/\s+/g, '-')
              .replace(/-+/g, '-')
              .trim();
            
            let targetElement = document.getElementById(slug);
            
            if (!targetElement) {
              let blockElement = null;
              blockElement = document.querySelector(`[data-rbd-draggable-id="${blockId}"]`);
              if (!blockElement) {
                blockElement = document.querySelector(`[class*="${blockId}"]`);
              }
              if (!blockElement) {
                blockElement = document.getElementById(blockId);
              }
              if (!blockElement) {
                const blockType = block['@type'];
                if (blockType) {
                  const candidates = document.querySelectorAll(`[class*="${blockType}"]`);
                  candidates.forEach((candidate) => {
                    if (candidate.textContent.includes(titleField.trim())) {
                      blockElement = candidate;
                    }
                  });
                }
              }
              
              if (blockElement) {
                blockElement.id = slug;
                blockElement.setAttribute('data-hero-toc-anchor', 'true');
              }
            }
          }
        });
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [data?.useTOC, data?.useBlockTitles, properties?.blocks, properties?.blocks_layout]);

  // Determine display mode
  const displayMode = data.displayMode || 'individual';
  const carouselEnabled = data.carouselEnabled || false;
  const carouselDisplayMode = data.carouselDisplayMode || 'individual';
  
  // Build heroes array
  let heroes = data.heroes || [];
  
  // Always construct the first hero from root-level fields
  const firstHero = {
    preheader: data.preheader,
    title: data.title,
    subtitle: data.subtitle,
    backgroundImage: data.backgroundImage,
    backgroundVideo: data.backgroundVideo,
    backgroundPosition: data.backgroundPosition || 'center',
    backgroundColor: data.backgroundColor,
    foregroundColor: data.foregroundColor || 'light',
    buttons: data.buttons,
    alignment: data.alignment,
    horizontalLayout: data.horizontalLayout,
    topPaddingDesktop: data.topPaddingDesktop,
    topPaddingMobile: data.topPaddingMobile,
    useTOC: data.useTOC,
    useH2: data.useH2,
    useH3: data.useH3,
    useBlockTitles: data.useBlockTitles,
    smallButtons: data.smallButtons,
    heroLogo: data.heroLogo,
    logoPosition: data.logoPosition,
    logoSize: data.logoSize,
  };
  
  // Carousel mode: only use additional heroes
  if (carouselEnabled && heroes.length > 0) {
    heroes = heroes;
  } else if (data.multipleHeroes && heroes.length > 0) {
    // Regular multiple heroes: include first hero + additional heroes
    heroes = [firstHero, ...heroes];
  } else {
    // Single hero mode
    heroes = [firstHero];
  }

  const blocks = properties.blocks || {};
  const layout = properties.blocks_layout?.items || [];
  const baseUrl = properties?.['@id'] || '';

  // Calculate shared background if needed
  let sharedBackgroundUrl = '';
  let sharedVideoUrl = null;

  if ((displayMode === 'shared' || (carouselEnabled && carouselDisplayMode === 'shared')) && (carouselEnabled ? true : heroes.length > 0)) {
    const bgSourceHero = carouselEnabled ? firstHero : heroes[0];
    
    if (data.usePreviewImage) {
      sharedBackgroundUrl = getPreviewImageUrl(properties);
    } else {
      const image = bgSourceHero.backgroundImage?.[0];
      sharedBackgroundUrl = image ? `${image['@id']}/@@images/image` : '';
    }

    const rawId = bgSourceHero.backgroundVideo?.[0]?.['@id'] || '';
    const filename = rawId.split('/').filter(Boolean).pop();
    const isAlreadyDownloadUrl = rawId.includes('/@@download/');
    sharedVideoUrl = rawId && filename
      ? (isAlreadyDownloadUrl ? rawId : `${rawId}/@@download/file/${filename}`)
      : null;
  }

  const usePageOptions = {
    usePageTitle: data.usePageTitle,
    usePageDescription: data.usePageDescription,
    usePreviewImage: data.usePreviewImage,
    showPublicationDate: data.showPublicationDate,
    showBreadcrumbs: data.showBreadcrumbs,
  };

  const customClass = data.customClass || '';
  const isFullWidth = data.isFullWidth ? 'full-width' : '';
  const isMultiple = (data.multipleHeroes && heroes.length > 1) || carouselEnabled ? 'multiple-heroes' : '';
  const isCarousel = carouselEnabled ? 'carousel-mode' : '';

  // Carousel settings
  const autoplayEnabled = data.carouselAutoplay || false;
  const autoplayDelay = data.carouselAutoplayDelay || 5000;
  const hideNav = data.carouselHideNav || false;
  const hideButtons = data.carouselHideButtons || false;
  const controlsColor = data.carouselControlsColor || 'light';
  
  // Setup Embla if carousel mode
  const autoplayPlugin = autoplayEnabled ? Autoplay({ delay: autoplayDelay, stopOnInteraction: false }) : null;
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true },
    autoplayPlugin ? [autoplayPlugin] : []
  );

  // Determine if we should apply shared background to wrapper
  const activeDisplayMode = carouselEnabled ? carouselDisplayMode : displayMode;
  const applySharedBackground = activeDisplayMode === 'shared';

  // If carousel mode, only render carousel with additional heroes
  if (carouselEnabled) {
    return (
      <>
        {/* Carousel with additional heroes */}
        {heroes.length > 0 && (
          <div 
            className={`custom-hero-block ${isFullWidth} ${isMultiple} ${isCarousel} ${customClass}`.trim()}
            style={applySharedBackground && sharedBackgroundUrl && !sharedVideoUrl ? { 
              backgroundImage: `url(${sharedBackgroundUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: data.backgroundPosition || 'center'
            } : {}}
          >
            {applySharedBackground && sharedVideoUrl && (
              <video
                className="hero-shared-video-bg"
                autoPlay
                muted
                loop
                playsInline
                poster={sharedBackgroundUrl}
              >
                <source src={sharedVideoUrl} type="video/mp4" />
              </video>
            )}
            
            <div className="embla" ref={emblaRef}>
              <div className="embla__container">
                {heroes.map((hero, index) => (
                  <HeroItem
                    key={index}
                    hero={hero}
                    properties={properties}
                    isFirst={false}
                    usePageOptions={{}}
                    blocks={blocks}
                    layout={layout}
                    displayMode={carouselDisplayMode}
                    sharedBackgroundUrl={sharedBackgroundUrl}
                    sharedVideoUrl={sharedVideoUrl}
                    heroIndex={index + 1}
                    carouselMode={true}
                    breadcrumbItems={breadcrumbItems}
                    selfBlockId={selfBlockId}
                  />
                ))}
              </div>
            </div>
            
            <CarouselControls 
              emblaApi={emblaApi} 
              hideNav={hideNav} 
              hideButtons={hideButtons}
              controlsColor={controlsColor}
            />
          </div>
        )}
      </>
    );
  }

  // Regular non-carousel mode
  return (
    <div 
      className={`custom-hero-block ${isFullWidth} ${isMultiple} ${customClass}`.trim()}
      style={applySharedBackground && sharedBackgroundUrl && !sharedVideoUrl ? { 
        backgroundImage: `url(${sharedBackgroundUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: data.backgroundPosition || 'center'
      } : {}}
    >
      {applySharedBackground && sharedVideoUrl && (
        <video
          className="hero-shared-video-bg"
          autoPlay
          muted
          loop
          playsInline
          poster={sharedBackgroundUrl}
        >
          <source src={sharedVideoUrl} type="video/mp4" />
        </video>
      )}
      {heroes.map((hero, index) => (
        <HeroItem
          key={index}
          hero={hero}
          properties={properties}
          isFirst={index === 0}
          usePageOptions={usePageOptions}
          blocks={blocks}
          layout={layout}
          displayMode={displayMode}
          sharedBackgroundUrl={sharedBackgroundUrl}
          sharedVideoUrl={sharedVideoUrl}
          heroIndex={index}
          carouselMode={false}
          breadcrumbItems={breadcrumbItems}
          selfBlockId={selfBlockId}
        />
      ))}
    </div>
  );
};

export default View;
